import styles from './NewTaskAgent.module.css'
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {useTasks} from '@github-ui/agent-sessions/hooks/useTasks'
import {useEffect, useState} from 'react'
import {NewTaskChatInput, type DropdownRepository} from '@github-ui/agent-sessions/components/NewTaskChatInput'
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {RelayEnvironmentProvider} from 'react-relay'
import type {
  CopilotChatOrg,
  CopilotChatPayload,
  CopilotChatReference,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {ArrowLeftIcon, AgentIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton, Spinner, Stack} from '@primer/react'
import type {Repository} from '@github-ui/current-repository'

const environment = relayEnvironmentWithMissingFieldHandlerForNode()

interface chatPayload extends CopilotChatPayload {
  findFileWorkerPath: string
  refs?: CopilotChatReference[]
  ssoOrganizations?: CopilotChatOrg[]
}

export function NewAgentTaskButton({
  currentRepo,
  hideRepoSelector,
}: {
  currentRepo?: Repository
  hideRepoSelector?: boolean
}) {
  const [open, setOpen] = useState(false)

  return (
    <AnchoredOverlay
      width="large"
      align="end"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderAnchor={props => (
        <IconButton
          icon={AgentIcon}
          aria-label="New agent task"
          // We shouldn't need this but the typing requires something
          aria-labelledby={undefined}
          {...props}
        />
      )}
    >
      <ConsecutiveCAPI401sProvider>
        <NewAgentTaskContent
          currentRepo={currentRepo}
          onCreate={() => {
            setOpen(false)
            document.dispatchEvent(new CustomEvent('toggle-copilot-overlay'))
          }}
          hideRepoSelector={hideRepoSelector}
        />
      </ConsecutiveCAPI401sProvider>
    </AnchoredOverlay>
  )
}

export function NewAgentTaskContent({
  currentRepo,
  onCreate,
  onCancel,
  hideRepoSelector,
}: {
  currentRepo?: Repository
  onCreate?: () => void
  onCancel?: () => void
  hideRepoSelector?: boolean
}) {
  const [copilotChatPayload, setCopilotChatPayload] = useState<chatPayload | null>(null)

  useEffect(() => {
    async function getChatProvider(): Promise<void> {
      if (copilotChatPayload) {
        return
      }

      try {
        const resp = await verifiedFetchJSON('/github-copilot/chat')
        if (!resp.ok) {
          throw new Error(`Failed to fetch chat provider: ${resp.statusText}`)
        }
        const json = (await resp.json()) as chatPayload
        setCopilotChatPayload(json)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching Copilot Chat provider:', error)
      }
    }
    getChatProvider()
  }, [copilotChatPayload])

  const {createSession} = useTasks(
    copilotChatPayload?.apiURL
      ? {apiURL: copilotChatPayload.apiURL, sessionsPollingInterval: false}
      : {apiURL: '', sessionsPollingInterval: false},
  )

  const handleUserSubmit = async (content: string, selectedRepo: DropdownRepository | null, selectedBranch: string) => {
    const trimmedContent = content.trim()
    if (trimmedContent === '') return
    if (!selectedRepo) return
    createSession({
      nwo: selectedRepo.nameWithOwner,
      problemStatement: trimmedContent,
      baseRef: selectedBranch || selectedRepo.defaultBranch,
    })

    onCreate?.()
  }

  return (
    <>
      <Stack direction="horizontal" align="center" padding="normal" gap="normal">
        {onCancel && (
          <IconButton variant="invisible" aria-label="Back to tasks" icon={ArrowLeftIcon} onClick={onCancel} />
        )}
        <h2 className="f5">New agent task</h2>
      </Stack>
      <div className={styles.newTaskContent}>
        {copilotChatPayload ? (
          <RelayEnvironmentProvider environment={environment}>
            <CopilotChatProvider
              workerPath={copilotChatPayload.findFileWorkerPath}
              threadId={null}
              refs={copilotChatPayload.refs || []}
              mode="immersive"
              ssoOrganizations={copilotChatPayload.ssoOrganizations || []}
              copilotUpsellBannerDismissed
              copilotChatPayload={copilotChatPayload}
            >
              <NewTaskChatInput
                handleUserSubmit={handleUserSubmit}
                initialRepo={currentRepo}
                hideRepoSelector={hideRepoSelector}
              />
            </CopilotChatProvider>
          </RelayEnvironmentProvider>
        ) : (
          <Stack padding="spacious" justify="center" align="center">
            <Spinner size="small" srText="Loading Copilot Chat" />
          </Stack>
        )}
      </div>
    </>
  )
}

try{ NewAgentTaskButton.displayName ||= 'NewAgentTaskButton' } catch {}
try{ NewAgentTaskContent.displayName ||= 'NewAgentTaskContent' } catch {}